import escapeRegExp from 'common/util/escapeRegExp';
import getFirstName from 'common/util/getFirstName';

import type { StrippedUserWithAlias } from 'common/api/endpoints/users';

export const MentionRegex = /@{[a-z0-9]{24}\|(alias|first_name|full_name)}/gi;
const RenderModeRegex = /^(alias|first_name|full_name)$/;

export enum RenderValues {
  alias = 'alias',
  first_name = 'first_name',
  full_name = 'full_name',
}
export enum RenderTypes {
  aliasID = 'aliasID',
  userID = 'userID',
}

/**
 *
 * If mentions are anonymized and the user is not an admin, we want to show the alias instead of the full name.
 */
export const getMentionDisplayValue = (
  user: StrippedUserWithAlias | null,
  renderMode: RenderValues
) => {
  const renderer = {
    [RenderValues.alias]: () => user?.alias ?? null,
    [RenderValues.first_name]: () => (user?.name ? getFirstName(user.name) : null),
    [RenderValues.full_name]: () => user?.name ?? null,
  }[renderMode];

  if (!renderer || !user) {
    return null;
  }

  return renderer();
};

export const getIDFromMentionTag = (mentionTag: string) => {
  if (!mentionTag.match(MentionRegex)) {
    return null;
  }

  const match = mentionTag.match(/[0-9a-z]{24}/);
  const entityID = match ? match[0] : null;

  const mode = getModeFromMentionTag(mentionTag);

  if (!mode || !entityID) {
    return null;
  }

  return {
    entityID,
    type: mode === RenderValues.alias ? RenderTypes.aliasID : RenderTypes.userID,
  };
};

export const getModeFromMentionTag = (mentionTag: string) => {
  if (!mentionTag.match(MentionRegex)) {
    return null;
  }

  const [, secondPart] = mentionTag.split('|');
  const match = secondPart.replace('}', '').match(RenderModeRegex);
  const mode = match ? match[0] : null;

  return mode;
};

export const getMentionTag = (
  user: StrippedUserWithAlias,
  renderMode: RenderValues = RenderValues.full_name
) => {
  const entityID = renderMode === RenderValues.alias ? user.aliasID : user._id;
  return `@{${entityID}|${renderMode}}`;
};

// Input: "@{1abdb132b4|full_name} @{2abdb132b4|first_name}"
// Output: ['@{1abdb132b4|full_name}', '@{2abdb132b4|first_name}']
export function getMentions(string: string) {
  const matches = string.match(MentionRegex);
  if (!matches) {
    return [];
  }

  const mentionSet: Record<string, boolean> = {};
  const mentions: string[] = [];

  matches.forEach((match) => {
    const mention = match.slice(match.indexOf('@'), match.indexOf('}') + 1).toLowerCase();
    if (mentionSet[mention]) {
      return;
    }

    mentionSet[mention] = true;
    mentions.push(mention);
  });

  return mentions;
}

export function removeMentions(string: string) {
  const mentions = getMentions(string);
  return mentions.reduce(
    (acc, mention) => acc.replace(new RegExp(escapeRegExp(mention), 'g'), ''),
    string
  );
}
